import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { seoService, pagesService } from '../../../lib/services';

const MetaTagManager = () => {
  const [activeTab, setActiveTab] = useState('title');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: seoData, error: seoError } = await seoService?.getAll();
        if (seoError) throw seoError;

        if (seoData && seoData?.length > 0) {
          setPages(seoData);
        } else {
          // Fallback: load pages without SEO data
          const { data: pagesData } = await pagesService?.getAll();
          setPages((pagesData || [])?.map(p => ({
            id: p?.id,
            pageId: p?.id,
            page: { id: p?.id, title: p?.title, slug: p?.slug, status: p?.status },
            metaTitle: '',
            metaDescription: '',
            ogTitle: '',
            ogDescription: '',
            ogImage: '',
            robotsIndex: true,
            robotsFollow: true,
          })));
        }
      } catch (err) {
        setError(err?.message || 'Failed to load SEO data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getStatusColor = (metaTitle, metaDescription) => {
    const titleLen = metaTitle?.length || 0;
    const descLen = metaDescription?.length || 0;
    if (titleLen >= 50 && titleLen <= 60 && descLen >= 120 && descLen <= 160) return 'bg-green-100 text-green-700';
    if (titleLen > 0 && descLen > 0) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const getStatusLabel = (metaTitle, metaDescription) => {
    const titleLen = metaTitle?.length || 0;
    const descLen = metaDescription?.length || 0;
    if (titleLen >= 50 && titleLen <= 60 && descLen >= 120 && descLen <= 160) return 'Optimized';
    if (titleLen > 0 && descLen > 0) return 'Warning';
    return 'Needs Attention';
  };

  const getLengthColor = (length, type) => {
    if (type === 'title') {
      if (length >= 50 && length <= 60) return 'text-green-600';
      if (length >= 40 && length < 70) return 'text-amber-600';
      return 'text-red-600';
    } else {
      if (length >= 120 && length <= 160) return 'text-green-600';
      if (length >= 100 && length < 180) return 'text-amber-600';
      return 'text-red-600';
    }
  };

  const startEdit = (page) => {
    setEditingId(page?.id);
    setEditValues({
      metaTitle: page?.metaTitle || '',
      metaDescription: page?.metaDescription || '',
      ogTitle: page?.ogTitle || '',
      ogDescription: page?.ogDescription || '',
      ogImage: page?.ogImage || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (page) => {
    setSavingId(page?.id);
    try {
      const pageId = page?.pageId || page?.page?.id;
      const { error: saveError } = await seoService?.upsert(pageId, editValues);
      if (saveError) throw saveError;

      setPages(prev => prev?.map(p =>
        p?.id === page?.id ? { ...p, ...editValues } : p
      ));
      setEditingId(null);
      setEditValues({});
      setSaveSuccess(page?.id);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save SEO data');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-blue-600 animate-spin mr-2" />
        <span className="text-gray-500">Loading SEO metadata...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Meta Tag Manager</h2>
            <p className="text-sm text-gray-600">Optimize titles and descriptions for search engines</p>
          </div>
        </div>
        <span className="text-sm text-gray-500">{pages?.length} pages</span>
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['title', 'description', 'opengraph']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab === 'title' ? 'Title Tags' : tab === 'description' ? 'Meta Descriptions' : 'Open Graph'}
          </button>
        ))}
      </div>
      {pages?.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm border border-gray-200 rounded-lg">
          No pages found. Create pages in the Page Manager to manage their SEO metadata.
        </div>
      ) : (
        <div className="space-y-4">
          {pages?.map((page) => {
            const isEditing = editingId === page?.id;
            const pageTitle = page?.page?.title || 'Untitled';
            const pageSlug = page?.page?.slug || '/';
            const metaTitle = isEditing ? editValues?.metaTitle : (page?.metaTitle || '');
            const metaDescription = isEditing ? editValues?.metaDescription : (page?.metaDescription || '');

            return (
              <div key={page?.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm md:text-base font-semibold text-gray-900">{pageTitle}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(page?.metaTitle, page?.metaDescription)}`}>
                        {getStatusLabel(page?.metaTitle, page?.metaDescription)}
                      </span>
                      {saveSuccess === page?.id && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Icon name="Check" size={12} /> Saved
                        </span>
                      )}
                    </div>
                    <p className="text-xs md:text-sm text-gray-500">{pageSlug}</p>
                  </div>
                  {!isEditing ? (
                    <Button variant="ghost" iconName="Edit2" size="sm" onClick={() => startEdit(page)}>
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Button>
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Save"
                        iconPosition="left"
                        loading={savingId === page?.id}
                        onClick={() => handleSave(page)}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                {activeTab === 'title' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs md:text-sm font-medium text-gray-700">Title Tag</label>
                      <span className={`text-xs font-medium ${getLengthColor(metaTitle?.length, 'title')}`}>
                        {metaTitle?.length} characters
                      </span>
                    </div>
                    {isEditing ? (
                      <Input
                        type="text"
                        value={editValues?.metaTitle}
                        onChange={(e) => setEditValues(prev => ({ ...prev, metaTitle: e?.target?.value }))}
                        placeholder="Enter meta title (50-60 chars recommended)"
                        className="text-sm"
                      />
                    ) : (
                      <div className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 min-h-[38px]">
                        {metaTitle || <span className="text-gray-400 italic">No title set</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Icon name="Info" size={14} />
                      <span>Optimal length: 50-60 characters</span>
                    </div>
                  </div>
                )}

                {activeTab === 'description' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs md:text-sm font-medium text-gray-700">Meta Description</label>
                      <span className={`text-xs font-medium ${getLengthColor(metaDescription?.length, 'description')}`}>
                        {metaDescription?.length} characters
                      </span>
                    </div>
                    {isEditing ? (
                      <textarea
                        value={editValues?.metaDescription}
                        onChange={(e) => setEditValues(prev => ({ ...prev, metaDescription: e?.target?.value }))}
                        rows={3}
                        placeholder="Enter meta description (120-160 chars recommended)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 min-h-[72px]">
                        {metaDescription || <span className="text-gray-400 italic">No description set</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Icon name="Info" size={14} />
                      <span>Optimal length: 120-160 characters</span>
                    </div>
                  </div>
                )}

                {activeTab === 'opengraph' && (
                  <div className="space-y-3">
                    {isEditing ? (
                      <>
                        <Input
                          label="OG Title"
                          type="text"
                          value={editValues?.ogTitle}
                          onChange={(e) => setEditValues(prev => ({ ...prev, ogTitle: e?.target?.value }))}
                          placeholder="Open Graph title"
                        />
                        <div>
                          <label className="text-xs md:text-sm font-medium text-gray-700 block mb-1">OG Description</label>
                          <textarea
                            value={editValues?.ogDescription}
                            onChange={(e) => setEditValues(prev => ({ ...prev, ogDescription: e?.target?.value }))}
                            rows={2}
                            placeholder="Open Graph description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <Input
                          label="OG Image URL"
                          type="url"
                          value={editValues?.ogImage}
                          onChange={(e) => setEditValues(prev => ({ ...prev, ogImage: e?.target?.value }))}
                          placeholder="https://example.com/og-image.jpg"
                        />
                      </>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs text-gray-500">OG Title: <span className="text-gray-700">{page?.ogTitle || <span className="italic">Not set</span>}</span></div>
                        <div className="text-xs text-gray-500">OG Description: <span className="text-gray-700">{page?.ogDescription || <span className="italic">Not set</span>}</span></div>
                        <div className="text-xs text-gray-500">OG Image: <span className="text-gray-700 break-all">{page?.ogImage || <span className="italic">Not set</span>}</span></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MetaTagManager;